import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/usePermissions';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import { ageFromDob, sexLabelKey } from '@/features/patients/patientDisplay';
import { useAptTickets, useCancelTicket, type AptTicketListItem } from './useAptTickets';

interface TodayQueueCardProps {
  /** The number on the waiting-room display — its holder's row is marked. */
  currentNumber: number;
}

/**
 * Who holds which number today, in number order.
 *
 * The counter above this card is a bare number; this is what says whose it is.
 * Cancelled tickets stay listed, struck through: their numbers are never
 * reused, so a gap with no explanation would read as a lost patient.
 */
export function TodayQueueCard({ currentNumber }: TodayQueueCardProps) {
  const { t } = useTranslation();
  const canManage = useHasPermission('apt_counter.manage');
  const { data, isLoading, isError } = useAptTickets();
  const cancel = useCancelTicket();
  // The ticket whose Cancel has been pressed once. Cancelling takes a second,
  // deliberate press: the number is retired for the day and cannot be restored.
  const [confirming, setConfirming] = useState<string | null>(null);

  const tickets = data?.tickets ?? [];
  const waiting = tickets.filter((x) => x.status === 'waiting').length;

  return (
    <section className="rounded-lg border border-line bg-paper" aria-labelledby="today-queue-title">
      <header className="flex items-center gap-3 border-b border-line px-4 py-3.5">
        <h2 id="today-queue-title" className="flex-1 text-[14px] font-semibold tracking-[-0.01em]">
          {t('aptCounter.todaysQueue')}
        </h2>
        {tickets.length > 0 && (
          <span className="text-[12px] text-muted tabular-nums">
            {t('aptCounter.waitingCount', { count: waiting })}
          </span>
        )}
      </header>

      {isLoading ? (
        <div className="px-4 py-6 text-[13px] text-muted">{t('common.loading')}</div>
      ) : isError ? (
        <div role="alert" className="px-4 py-6 text-[13px] text-destructive">
          {t('aptCounter.couldNotLoadQueue')}
        </div>
      ) : tickets.length === 0 ? (
        <div className="px-4 py-8 text-center text-[13px] text-muted">
          {t('aptCounter.queueEmpty')}
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {tickets.map((ticket) => (
            <li key={ticket.id}>
              <QueueRow
                ticket={ticket}
                current={ticket.status === 'waiting' && ticket.number === currentNumber}
                canCancel={canManage && ticket.status === 'waiting'}
                confirming={confirming === ticket.id}
                busy={cancel.isPending}
                onAskCancel={() => setConfirming(ticket.id)}
                onKeep={() => setConfirming(null)}
                onConfirmCancel={() =>
                  cancel.mutate(ticket.id, { onSettled: () => setConfirming(null) })
                }
              />
            </li>
          ))}
        </ul>
      )}

      {cancel.isError && (
        <div role="alert" className="border-t border-line px-4 py-2.5 text-[12.5px] text-destructive">
          {translateApiError(cancel.error)}
        </div>
      )}
    </section>
  );
}

interface QueueRowProps {
  ticket: AptTicketListItem;
  current: boolean;
  canCancel: boolean;
  confirming: boolean;
  busy: boolean;
  onAskCancel: () => void;
  onKeep: () => void;
  onConfirmCancel: () => void;
}

function QueueRow({
  ticket,
  current,
  canCancel,
  confirming,
  busy,
  onAskCancel,
  onKeep,
  onConfirmCancel,
}: QueueRowProps) {
  const { t } = useTranslation();
  const cancelled = ticket.status === 'cancelled';
  const age = ageFromDob(ticket.patient.dateOfBirth);
  const demographics = [
    age != null ? `${age} ${t('patients.yrs')}` : null,
    t(sexLabelKey(ticket.patient.sex)),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={cn('flex items-center gap-3 px-4 py-3', current && 'bg-paper-2')}>
      <span
        className={cn(
          'w-10 shrink-0 text-center font-mono text-[18px] font-bold tabular-nums',
          cancelled && 'text-muted-2 line-through',
        )}
      >
        {ticket.number}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate text-[13.5px] font-semibold tracking-[-0.01em]',
            cancelled && 'text-muted line-through',
          )}
        >
          {ticket.patient.fullName}
        </div>
        <div className="text-[12px] text-muted">{demographics}</div>
      </div>
      {current && (
        <Badge tone="success" size="md">
          {t('aptCounter.nowServing')}
        </Badge>
      )}
      {cancelled && (
        <Badge tone="neutral" size="md">
          {t('aptCounter.statusCancelled')}
        </Badge>
      )}
      {ticket.status === 'recorded' && (
        <Badge tone="info" size="md">
          {t('aptCounter.statusRecorded')}
        </Badge>
      )}
      {canCancel &&
        (confirming ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={onKeep} disabled={busy}>
              {t('aptCounter.keepNumber')}
            </Button>
            <Button variant="danger" size="sm" onClick={onConfirmCancel} disabled={busy}>
              {t('aptCounter.confirmCancelNumber', { number: ticket.number })}
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAskCancel}
            aria-label={t('aptCounter.cancelNumberFor', {
              number: ticket.number,
              name: ticket.patient.fullName,
            })}
          >
            {t('common.cancel')}
          </Button>
        ))}
    </div>
  );
}
